import { useState, useEffect } from 'react';
import { api } from '../../../api';
import type { DomainLocation, OnboardingStatus, ReachProfile } from '../../../types';
import { ReachPicker } from './ReachPicker';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  domainId: string;
  domain: string;
  tier: string;
  status: OnboardingStatus;
  onStatusUpdate: (next: OnboardingStatus) => void;
  onContinue: () => void;
  onSkip: () => void;
}

interface LocationEditState {
  id: string;
  city: string;
  countryCode: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function MarketContextStep({ domainId, domain, tier, status, onStatusUpdate, onContinue, onSkip }: Props) {
  // ── Reach state ───────────────────────────────────────────────────────────
  const [reach, setReach] = useState<ReachProfile | null>(null);
  const [reachSaving, setReachSaving] = useState(false);
  const [reachSaved, setReachSaved] = useState(false);
  const [reachError, setReachError] = useState('');

  // ── Locations state ───────────────────────────────────────────────────────
  const [locations, setLocations] = useState<DomainLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState('');
  const [addCity, setAddCity] = useState('');
  const [addCountry, setAddCountry] = useState('');
  const [addPrimary, setAddPrimary] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [editState, setEditState] = useState<LocationEditState | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [reachLocationWarning, setReachLocationWarning] = useState('');

  // ── Audience state ────────────────────────────────────────────────────────
  const [audience, setAudience] = useState<'b2c' | 'b2b' | 'both' | null>(null);
  const [audienceSaving, setAudienceSaving] = useState(false);
  const [audienceSaved, setAudienceSaved] = useState(false);
  const [audienceError, setAudienceError] = useState('');

  // ── Initial data load ─────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.domains.get(domainId),
      api.locations.list(domainId),
      api.domains.getBusinessContext(domainId),
    ])
      .then(([domainDetail, locs, bc]) => {
        setReach(domainDetail.reachProfile);
        setLocations(locs);
        if (bc) setAudience(bc.targetAudience);
      })
      .catch(() => setLocationsError('Failed to load profile data'))
      .finally(() => setLocationsLoading(false));
  }, [domainId]);

  // ── Reach-vs-location warning ─────────────────────────────────────────────
  useEffect(() => {
    if (!reach || locations.length === 0) {
      setReachLocationWarning('');
      return;
    }
    const primary = locations.find((l) => l.isPrimary);
    if (!primary) {
      if (reach === 'local' || reach === 'national') {
        setReachLocationWarning(`Add a primary location to match your ${reach} reach setting.`);
      } else {
        setReachLocationWarning('');
      }
      return;
    }
    // Warn if local reach but primary location has no city
    if (reach === 'local' && !primary.city) {
      setReachLocationWarning('Your primary location has no city — local reach scans work best with a city set.');
    } else {
      setReachLocationWarning('');
    }
  }, [reach, locations]);

  // ── Reach handlers ────────────────────────────────────────────────────────
  async function handleReachSave() {
    if (!reach) return;
    setReachSaving(true);
    setReachError('');
    try {
      await api.reach.set(domainId, reach);
      await api.onboarding.patchProgress(domainId, {
        reach_profile: true,
        ...(reach === 'global' ? { primary_location: true } : {}),
      });
      const nextStatus = await api.onboarding.getStatus(domainId);
      onStatusUpdate(nextStatus);
      setReachSaved(true);
    } catch {
      setReachError('Failed to save reach. Please try again.');
    } finally {
      setReachSaving(false);
    }
  }

  // ── Locations handlers ────────────────────────────────────────────────────
  async function handleAddLocation() {
    const isGlobal = reach === 'global';
    if ((!isGlobal && !addCity.trim()) || !addCountry.trim() || addCountry.trim().length !== 2) {
      setAddError(isGlobal ? 'Enter a 2-letter country code (e.g. US, GB).' : 'Enter a valid city and 2-letter country code.');
      return;
    }
    setAddSaving(true);
    setAddError('');
    try {
      const hasPrimary = locations.some((l) => l.isPrimary);
      const isPrimary = addPrimary || (!hasPrimary);
      const newLoc = await api.locations.add(domainId, {
        locationCode: addCountry.trim().toUpperCase(),
        countryCode:  addCountry.trim().toUpperCase(),
        city:         addCity.trim(),
        isPrimary,
      });
      const updated = isPrimary
        ? [...locations.map((l) => ({ ...l, isPrimary: false })), newLoc]
        : [...locations, newLoc];
      setLocations(updated);
      setAddCity('');
      setAddCountry('');
      setAddPrimary(false);
      // Mark primary_location progress if this was the first primary
      if (isPrimary) {
        await api.onboarding.patchProgress(domainId, { primary_location: true });
        const nextStatus = await api.onboarding.getStatus(domainId);
        onStatusUpdate(nextStatus);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add location';
      setAddError(msg.includes('plan allows') ? msg : 'Failed to add location');
    } finally {
      setAddSaving(false);
    }
  }

  async function handleSetPrimary(locationId: string) {
    try {
      await api.locations.setPrimary(domainId, locationId);
      setLocations((prev) =>
        prev.map((l) => ({ ...l, isPrimary: l.id === locationId })),
      );
      await api.onboarding.patchProgress(domainId, { primary_location: true });
      const nextStatus = await api.onboarding.getStatus(domainId);
      onStatusUpdate(nextStatus);
    } catch {
      // non-fatal
    }
  }

  async function handleDeleteLocation(locationId: string) {
    try {
      await api.locations.remove(domainId, locationId);
      setLocations((prev) => prev.filter((l) => l.id !== locationId));
    } catch {
      // non-fatal
    }
  }

  function startEdit(loc: DomainLocation) {
    setEditState({ id: loc.id, city: loc.city ?? '', countryCode: loc.countryCode });
    setEditError('');
  }

  async function handleEditSave() {
    if (!editState) return;
    if (!editState.city.trim() || editState.countryCode.trim().length !== 2) {
      setEditError('Enter a valid city and 2-letter country code.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const updated = await api.locations.update(domainId, editState.id, {
        city:        editState.city.trim(),
        countryCode: editState.countryCode.trim().toUpperCase(),
      });
      setLocations((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      setEditState(null);
    } catch {
      setEditError('Failed to update. Please try again.');
    } finally {
      setEditSaving(false);
    }
  }

  // ── Audience handler ──────────────────────────────────────────────────────
  async function handleAudienceSave() {
    if (!audience) return;
    setAudienceSaving(true);
    setAudienceError('');
    try {
      await api.domains.patchAudienceContext(domainId, audience);
      await api.onboarding.patchProgress(domainId, { audience_confirmed: true });
      const nextStatus = await api.onboarding.getStatus(domainId);
      onStatusUpdate(nextStatus);
      setAudienceSaved(true);
    } catch {
      setAudienceError('Failed to save audience. Please try again.');
    } finally {
      setAudienceSaving(false);
    }
  }

  // ── Continue enabled if any of the three has been saved ──────────────────
  const canContinue =
    status.progress.reach_profile === true ||
    status.progress.primary_location === true ||
    status.progress.audience_confirmed === true ||
    reachSaved ||
    audienceSaved;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="market-context-step">
      <div>
        <h2 className="text-xl font-semibold text-brand-ink">Market Context</h2>
        <p className="text-sm text-brand-muted mt-1">
          Tell us where and who you serve. Each section can be saved independently.
        </p>
      </div>

      {/* ── Reach card ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4" data-testid="reach-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-brand-ink text-sm">Geographic reach</h3>
            <p className="text-xs text-brand-muted mt-0.5">
              Drives SERP scan locations, GEO queries, and competitor discovery scope.
            </p>
          </div>
          {status.progress.reach_profile && (
            <span className="text-xs text-brand-green font-medium bg-green-50 px-2 py-0.5 rounded-full">Saved</span>
          )}
        </div>

        <ReachPicker value={reach} tier={tier} onChange={setReach} />

        {reachLocationWarning && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="reach-location-warning">
            {reachLocationWarning}
          </p>
        )}

        {reachError && <p className="text-xs text-red-500">{reachError}</p>}

        <button
          type="button"
          data-testid="save-reach-btn"
          disabled={!reach || reachSaving}
          onClick={() => void handleReachSave()}
          className="px-4 py-2 bg-brand-green text-white rounded-lg text-sm font-medium hover:bg-brand-green/90 disabled:opacity-50"
        >
          {reachSaving ? 'Saving…' : 'Save reach'}
        </button>
      </div>

      {/* ── Locations card ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4" data-testid="locations-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-brand-ink text-sm">Locations</h3>
            <p className="text-xs text-brand-muted mt-0.5">
              {reach === 'global'
                ? `Optionally add country markets for ${domain} to prioritise in GEO sweeps.`
                : reach === 'local' || reach === 'national'
                ? `Add the cities and countries where ${domain} operates. A primary location is required for this reach setting.`
                : `Add the cities and countries where ${domain} operates.`}
            </p>
          </div>
          {status.progress.primary_location && (
            <span className="text-xs text-brand-green font-medium bg-green-50 px-2 py-0.5 rounded-full">Saved</span>
          )}
        </div>

        {locationsLoading ? (
          <div className="animate-pulse h-10 bg-gray-100 rounded" data-testid="locations-loading" />
        ) : locationsError ? (
          <p className="text-xs text-red-500">{locationsError}</p>
        ) : (
          <>
            {/* Location list */}
            {locations.length > 0 && (
              <ul className="space-y-2" data-testid="location-list">
                {locations.map((loc) => (
                  <li
                    key={loc.id}
                    className="flex items-center gap-2 text-sm border border-gray-100 rounded-lg px-3 py-2"
                    data-testid={`location-row-${loc.id}`}
                  >
                    {editState?.id === loc.id ? (
                      /* Inline editor */
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        <input
                          type="text"
                          data-testid="edit-city-input"
                          value={editState.city}
                          onChange={(e) => setEditState({ ...editState, city: e.target.value })}
                          placeholder="City"
                          className="border border-gray-200 rounded px-2 py-1 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-brand-green"
                        />
                        <input
                          type="text"
                          data-testid="edit-country-input"
                          value={editState.countryCode}
                          onChange={(e) => setEditState({ ...editState, countryCode: e.target.value.toUpperCase().slice(0, 2) })}
                          placeholder="CC"
                          maxLength={2}
                          className="border border-gray-200 rounded px-2 py-1 text-xs w-14 focus:outline-none focus:ring-1 focus:ring-brand-green"
                        />
                        {editError && <span className="text-xs text-red-500">{editError}</span>}
                        <button
                          type="button"
                          data-testid="edit-save-btn"
                          disabled={editSaving}
                          onClick={() => void handleEditSave()}
                          className="text-xs text-brand-green hover:underline disabled:opacity-50"
                        >
                          {editSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditState(null)}
                          className="text-xs text-brand-muted hover:text-brand-ink"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="flex-1 text-brand-ink">
                          {loc.city ? `${loc.city}, ` : ''}{loc.countryCode}
                        </span>
                        {loc.isPrimary && (
                          <span className="text-[11px] text-brand-green bg-green-50 px-1.5 py-0.5 rounded font-medium">
                            Primary
                          </span>
                        )}
                        {!loc.isPrimary && (
                          <button
                            type="button"
                            data-testid={`set-primary-${loc.id}`}
                            onClick={() => void handleSetPrimary(loc.id)}
                            className="text-xs text-brand-muted hover:text-brand-green"
                            title="Set as primary"
                          >
                            ★
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`edit-location-${loc.id}`}
                          onClick={() => startEdit(loc)}
                          className="text-xs text-brand-muted hover:text-brand-ink"
                          title="Edit"
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          data-testid={`delete-location-${loc.id}`}
                          onClick={() => void handleDeleteLocation(loc.id)}
                          className="text-xs text-brand-muted hover:text-red-500"
                          title="Delete"
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Add location form */}
            <div className="flex items-start gap-2 flex-wrap" data-testid="add-location-form">
              {reach !== 'global' && (
                <input
                  type="text"
                  data-testid="add-city-input"
                  value={addCity}
                  onChange={(e) => setAddCity(e.target.value)}
                  placeholder="City"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-brand-green"
                />
              )}
              <input
                type="text"
                data-testid="add-country-input"
                value={addCountry}
                onChange={(e) => setAddCountry(e.target.value.toUpperCase().slice(0, 2))}
                placeholder={reach === 'global' ? 'Country code (e.g. US)' : 'CC'}
                maxLength={2}
                className={`border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green ${reach === 'global' ? 'w-48' : 'w-16'}`}
              />
              {reach !== 'global' && (
                <label className="flex items-center gap-1.5 text-xs text-brand-muted self-center">
                  <input
                    type="checkbox"
                    data-testid="add-primary-checkbox"
                    checked={addPrimary}
                    onChange={(e) => setAddPrimary(e.target.checked)}
                    className="rounded"
                  />
                  Primary
                </label>
              )}
              <button
                type="button"
                data-testid="add-location-btn"
                disabled={addSaving}
                onClick={() => void handleAddLocation()}
                className="px-3 py-2 border border-brand-green text-brand-green rounded-lg text-sm hover:bg-green-50 disabled:opacity-50"
              >
                {addSaving ? 'Adding…' : '+ Add'}
              </button>
            </div>
            {addError && <p className="text-xs text-red-500" data-testid="add-location-error">{addError}</p>}
          </>
        )}
      </div>

      {/* ── Audience card ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4" data-testid="audience-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-brand-ink text-sm">Target audience</h3>
            <p className="text-xs text-brand-muted mt-0.5">
              We use this to tune the questions we ask AI engines about your business.
            </p>
          </div>
          {status.progress.audience_confirmed && (
            <span className="text-xs text-brand-green font-medium bg-green-50 px-2 py-0.5 rounded-full">Saved</span>
          )}
        </div>

        <div className="flex gap-3 flex-wrap" data-testid="audience-radios">
          {(['b2c', 'b2b', 'both'] as const).map((val) => {
            const labels: Record<string, string> = { b2c: 'B2C', b2b: 'B2B', both: 'Both' };
            const isSelected = audience === val;
            return (
              <button
                key={val}
                type="button"
                data-testid={`audience-option-${val}`}
                onClick={() => setAudience(val)}
                aria-pressed={isSelected}
                className={`px-5 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                  isSelected
                    ? 'border-brand-green bg-green-50 text-brand-green'
                    : 'border-gray-200 text-brand-ink hover:border-gray-300'
                }`}
              >
                {labels[val]}
              </button>
            );
          })}
        </div>

        {audienceError && <p className="text-xs text-red-500">{audienceError}</p>}

        <button
          type="button"
          data-testid="save-audience-btn"
          disabled={!audience || audienceSaving}
          onClick={() => void handleAudienceSave()}
          className="px-4 py-2 bg-brand-green text-white rounded-lg text-sm font-medium hover:bg-brand-green/90 disabled:opacity-50"
        >
          {audienceSaving ? 'Saving…' : 'Save audience'}
        </button>
      </div>

      {/* ── Footer navigation ───────────────────────────────────────────── */}
      <div className="flex items-center gap-4 pt-2">
        <button
          type="button"
          data-testid="continue-btn"
          disabled={!canContinue}
          onClick={onContinue}
          className="px-5 py-2.5 bg-brand-green text-white rounded-lg text-sm font-medium hover:bg-brand-green/90 disabled:opacity-40"
        >
          Continue to step 3
        </button>
        <button
          type="button"
          data-testid="skip-btn"
          onClick={onSkip}
          className="text-sm text-brand-muted hover:text-brand-ink"
        >
          I'll come back to this
        </button>
      </div>
    </div>
  );
}
